import type { Application, ApplicationStage, EligibilityTier } from '../types/application';

export type DashboardButtonType = 'primary' | 'secondary' | 'bluefil';

export interface DashboardCardButton {
  label: string;
  type: DashboardButtonType;
  link: string;
}

export interface EligibilityCardCTA {
  buttons: DashboardCardButton[];
  downloadOnly: boolean;
}

export interface ApplicationCardCTA {
  statusLabel: string;
  button: DashboardCardButton;
  /** Extra standalone "Pay USCIS fee" CTA shown when the fee is still payable. */
  extraButton?: DashboardCardButton;
}

const ATTORNEY_LINK = '/connect-attorney';

const ATTORNEY_BUTTON: DashboardCardButton = {
  label: 'Contact Attorney',
  type: 'bluefil',
  link: ATTORNEY_LINK,
};

/** Chip copy and colours per tier, mirroring the eligibility result screen. */
export const ELIGIBILITY_TIER_CHIP: Record<
  EligibilityTier,
  { label: string; bg: string; border: string; text: string; dot: string }
> = {
  green: { label: 'Eligible', bg: '#DCFCE7', border: '#DCFCE7', text: '#166534', dot: '#41C700' },
  yellow: {
    label: 'Review Recommended',
    bg: '#FEF3C7',
    border: '#FDE68A',
    text: '#92400E',
    dot: '#CA850B',
  },
  red: { label: 'Concerns Identified', bg: '#FEE2E2', border: '#FECACA', text: '#991B1B', dot: '#EF4444' },
};

/**
 * The stored tier, falling back to the boolean for applications scored before
 * eligibility_status existed.
 */
export function getEligibilityTier(app: Application): EligibilityTier | null {
  if (app.eligibilityStatus) return app.eligibilityStatus;
  if (app.isQualified === true) return 'green';
  if (app.isQualified === false) return 'red';
  return null;
}

/**
 * Yellow and red both carry an attorney recommendation. It is advice shown
 * alongside the normal action, never a replacement for it — all three tiers
 * stay free to continue preparing their forms.
 */
function needsAttorneyAdvice(app: Application): boolean {
  const tier = getEligibilityTier(app);
  return tier === 'yellow' || tier === 'red';
}

/** Appends the attorney recommendation unless one of the buttons already links there. */
function withAttorneyAdvice(
  app: Application,
  buttons: DashboardCardButton[]
): EligibilityCardCTA {
  const alreadyLinked = buttons.some((b) => b.link === ATTORNEY_LINK);
  return {
    buttons: needsAttorneyAdvice(app) && !alreadyLinked ? [...buttons, ATTORNEY_BUTTON] : buttons,
    downloadOnly: false,
  };
}

const STARTED_STAGES: ApplicationStage[] = [
  'payment_completed',
  'forms_in_progress',
  'forms_completed',
  'under_review',
  'revision_requested',
  'ready_for_uscis',
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
  'submitted_to_uscis',
  'approved',
  'rejected',
  'closed',
];

const STUCK_PAYMENT_THRESHOLD_MS = 15 * 60 * 1000;

function getAppLink(app: Application, opts?: { focusDocuments?: boolean }): string {
  const base = `/visa-application?applicationId=${app.id}&visaType=${app.visaType}`;
  return opts?.focusDocuments ? `${base}&focus=documents` : base;
}

function isPaymentStuck(updatedAt: string | null | undefined, nowMs: number): boolean {
  if (!updatedAt) return false;
  const parsed = Date.parse(updatedAt);
  if (!Number.isFinite(parsed)) return false;
  return nowMs - parsed > STUCK_PAYMENT_THRESHOLD_MS;
}

export function hasStartedApplications(applications: Application[]): boolean {
  return applications.some((app) => STARTED_STAGES.includes(app.stage));
}

export function isApplicationStarted(app: Application): boolean {
  return STARTED_STAGES.includes(app.stage);
}

export function getEligibilityCardCTA(app: Application, nowMs: number = Date.now()): EligibilityCardCTA {
  if (STARTED_STAGES.includes(app.stage)) {
    return { buttons: [], downloadOnly: true };
  }

  if (app.stage === 'payment_pending') {
    if (app.latestPaymentStatus === 'failed') {
      return withAttorneyAdvice(app, [
        { label: 'Retry Payment', type: 'primary', link: `/application/${app.id}/uscis-fee` },
      ]);
    }
    if (app.latestPaymentStatus === 'pending' && isPaymentStuck(app.latestPaymentUpdatedAt, nowMs)) {
      return withAttorneyAdvice(app, [
        { label: 'Contact Support', type: 'secondary', link: ATTORNEY_LINK },
      ]);
    }
    return withAttorneyAdvice(app, [
      { label: 'Start Application', type: 'primary', link: `/application/${app.id}/uscis-fee` },
    ]);
  }

  if (
    app.stage === 'package_selected' ||
    app.stage === 'uscis_fee_decision' ||
    app.stage === 'uscis_fee_questionnaire' ||
    app.stage === 'uscis_fee_ready'
  ) {
    const link =
      app.stage === 'package_selected'
        ? `/subscription-package?applicationId=${app.id}&visaType=${app.visaType}`
        : `/application/${app.id}/uscis-fee`;
    return withAttorneyAdvice(app, [{ label: 'Start Application', type: 'primary', link }]);
  }

  // 'disqualified' sits here too: the eligibility step is finished either way,
  // so a red applicant picks a package exactly like everyone else.
  if (
    app.stage === 'qualified' ||
    app.stage === 'disqualified' ||
    app.isQualified === true
  ) {
    return withAttorneyAdvice(app, [
      {
        label: 'Pay Now',
        type: 'primary',
        link: `/subscription-package?applicationId=${app.id}&visaType=${app.visaType}`,
      },
    ]);
  }

  return withAttorneyAdvice(app, []);
}

/**
 * Stages the applicant still owns: forms or documents unfinished, or finished
 * but not yet sent with Submit for Review (forms_completed).
 */
const INCOMPLETE_STAGES: ApplicationStage[] = ['payment_completed', 'forms_in_progress', 'forms_completed'];

/**
 * Everything between Submit for Review and filing is the reviewer's work, so
 * the applicant sees one status for all of it. Form approval, docket
 * generation and the signature placeholders are internal steps — the
 * signature itself is captured inside the form, so there is nothing to upload.
 */
const UNDER_REVIEW_STAGES: ApplicationStage[] = [
  'under_review',
  'ready_for_uscis',
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
];

/** Stages after every form is approved, while filing waits on docs/fee/docket. */
const APPROVED_STAGES: ApplicationStage[] = [
  'ready_for_uscis',
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
];

/** Stages after the attorney generates the filing packet (docket). */
const PACKET_READY_STAGES: ApplicationStage[] = [
  'uscis_form_generated',
  'signature_pending',
  'signature_completed',
];

/**
 * Applicant-facing flow: Application Incomplete → Under Review →
 * Feedback Received → Under Review → Submitted to USCIS. Before approval an
 * unpaid pay-later USCIS fee adds a "Pay USCIS Fee" button via
 * getApplicationCardCTA; once approved it becomes the status itself
 * ("Action Needed: Pay USCIS Fee"), because filing then waits on it.
 */
function computeApplicationCardCTA(app: Application): ApplicationCardCTA {
  const appLink = getAppLink(app);
  const docsLink = getAppLink(app, { focusDocuments: true });
  const documentFeedbackCount = Number(app.documentFeedbackPendingCount) || 0;
  const formFeedbackCount = Number(app.feedbackPendingItemCount) || 0;

  if (app.stage === 'revision_requested' || formFeedbackCount > 0) {
    return {
      statusLabel: 'Feedback Received',
      button: {
        label: 'View Feedback',
        type: 'secondary',
        link: formFeedbackCount > 0 ? `${appLink}&openFeedback=1` : appLink,
      },
    };
  }

  if (documentFeedbackCount > 0 || app.hasDocumentFeedback) {
    return {
      statusLabel: 'Feedback Received',
      button: { label: 'View Documents', type: 'secondary', link: docsLink },
    };
  }

  if (INCOMPLETE_STAGES.includes(app.stage)) {
    return {
      statusLabel: 'Application Incomplete',
      button: { label: 'Continue Application', type: 'secondary', link: appLink },
    };
  }

  // Basic plan: the applicant downloads the packet and mails it to USCIS
  // themselves, so once it is generated their flow ends here rather than at
  // "Submitted to USCIS" (which only a Premium filing by LetsGetVisa reaches).
  if (app.package?.tier === 'basic' && PACKET_READY_STAGES.includes(app.stage)) {
    return {
      statusLabel: 'Ready to File',
      button: { label: 'Download Packet', type: 'primary', link: `${appLink}&focus=docket` },
    };
  }

  // Approved, but the applicant deferred the USCIS fee and has not paid it:
  // filing now waits on them, so say so instead of "Under Review".
  if (APPROVED_STAGES.includes(app.stage) && app.uscisFee?.canPayNow) {
    return {
      statusLabel: 'Action Needed: Pay USCIS Fee',
      button: { label: 'Pay USCIS Fee', type: 'primary', link: `/application/${app.id}/uscis-fee` },
    };
  }

  if (UNDER_REVIEW_STAGES.includes(app.stage)) {
    return {
      statusLabel: 'Under Review',
      button: { label: 'View Application', type: 'secondary', link: appLink },
    };
  }

  if (app.stage === 'submitted_to_uscis') {
    return {
      statusLabel: 'Submitted to USCIS',
      button: { label: 'View Application', type: 'secondary', link: appLink },
    };
  }

  if (app.stage === 'approved') {
    return {
      statusLabel: 'Approved',
      button: { label: 'View Details', type: 'secondary', link: appLink },
    };
  }

  if (app.stage === 'rejected') {
    return {
      statusLabel: 'Rejected',
      button: { label: 'Contact Attorney', type: 'bluefil', link: '/connect-attorney' },
    };
  }

  if (app.stage === 'closed') {
    return {
      statusLabel: 'Closed',
      button: { label: 'View Details', type: 'secondary', link: appLink },
    };
  }

  return {
    statusLabel: 'Application Incomplete',
    button: { label: 'Continue Application', type: 'secondary', link: appLink },
  };
}

/**
 * Card CTA for a started application, plus a standalone "Pay USCIS fee" button
 * whenever the fee is still payable (premium applicant who opted out earlier).
 * Skips the extra button when the base CTA is already a fee-payment link
 * (e.g. signature_completed) to avoid showing two "Pay" buttons.
 */
export function getApplicationCardCTA(app: Application): ApplicationCardCTA {
  const cta = computeApplicationCardCTA(app);
  if (app.uscisFee?.canPayNow && !cta.button.link.includes('/uscis-fee')) {
    cta.extraButton = {
      label: 'Pay USCIS Fee',
      type: 'primary',
      link: `/application/${app.id}/uscis-fee`,
    };
  }
  return cta;
}
